<script setup lang="ts">
import Icon from "@/core/components/icon/Icon.vue";
import GlowingContainer from "@/core/components/glowing-container/GlowingContainer.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import MarkdownView from "@/core/components/markdown/Markdown.vue";
import {onMounted, ref, nextTick} from "vue";
import {Knowledgeable} from "@/knowledge-box/interfaces";
import {useKnowledgeableStream} from "@/knowledge-box/composables/useKnowledgeableStream";
import KnowledgeableChatProducts from "@/knowledge-box/components/KnowledgeableChatProducts.vue";

const {knowledgeable} = defineProps<{
    knowledgeable: Knowledgeable;
}>();

const {
    chatHistory,
    isLoading,
    streamAsk,
    stopStream,
    addMessage
} = useKnowledgeableStream(knowledgeable.id);

const userInput = ref('');
const chatWindow = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);

onMounted(async () => {
    addMessage(
        "assistant",
        "Bonjour, comment puis-je vous aider ?"
    );

    await nextTick();
    inputRef.value?.focus();
});


const sendMessage = async (): Promise<void> => {
    if (!userInput.value || userInput.value.trim() === '') {
        return;
    }

    const currentMessage = userInput.value.trim();
    userInput.value = '';

    try {
        streamAsk(currentMessage, knowledgeable.can_query_products);
    } catch (error) {
        console.error('Error sending message:', error);
    }
};
</script>

<template>
  <div class="message-container bg-gray-100 flex flex-col rounded-md">
    <div class="flex-1 overflow-hidden">
      <div class="h-full flex flex-col pl-6 pt-6 pb-6">
        <div ref="chatWindow" class="overflow-y-auto">
          <div v-for="message in chatHistory" :key="message.id" class="mb-4">
            <div
              :class="[
                'max-w-xs md:max-w-md lg:max-w-lg xl:max-w-xl rounded-lg p-3',
                message.role === 'user' ? 'bg-blue-500 ml-auto mr-6' : 'bg-white'
              ]"
            >
              <div v-if="message.role === 'assistant'">
                <Icon name="mdi-robot-happy" class="ml-2" color="primary" />
              </div>
              <div>
                <!-- Written by a model: HTML in it is displayed, never executed. -->
                <MarkdownView
                  :class="[
                    message.role === 'user' ? 'text-white' : 'text-gray-800'
                  ]" :inherit-styles="true" :content="message.content" untrusted
                />
                <div v-if="message.citations?.length">
                  <div v-for="(citation, index) in message.citations" :key="message.id + '_' + index" class="mt-2">
                    <p
                      v-for="(reference, index) in citation.citation?.retrievedReferences ?? []"
                      :key="`${message.id}_reference_${index}`"
                      class="text-sm text-gray-600"
                    >
                      {{ reference.content.text }}
                    </p>
                  </div>
                </div>
              </div>
              <div v-if="message.products && message.products.length > 0" class="mt-2">
                <KnowledgeableChatProducts :products="message.products" />
              </div>
            </div>
          </div>
          <div v-if="isLoading" class="flex items-center space-x-1">
            <div class="w-3 h-3 bg-gray-500 rounded-full animate-bounce" />
            <div class="w-3 h-3 bg-gray-500 rounded-full animate-bounce" style="animation-delay: 0.1s" />
            <div class="w-3 h-3 bg-gray-500 rounded-full animate-bounce" style="animation-delay: 0.2s" />
          </div>
        </div>
      </div>
    </div>
    <div class="bg-white border-t border-gray-200 py-2">
      <form class="flex" @submit.prevent="sendMessage">
        <GlowingContainer class="w-full">
          <div class="flex items-center gap-3">
            <input
              ref="inputRef"
              v-model="userInput"
              type="text"
              :placeholder="knowledgeable?.default_prompt ?? 'Entrez votre message...'"
              class="flex-1 rounded-md px-4 py-2 focus:outline-none focus:ring-0"
            >
            <BaseButton v-if="!isLoading" variant="flat" type="submit">
              Demander
              <Icon name="mdi-creation" color="primary" class="ml-1" />
            </BaseButton>
            <BaseButton v-if="isLoading" variant="flat" @click="stopStream">
              <Icon name="mdi-stop-circle-outline" color="primary" />
            </BaseButton>
          </div>
        </GlowingContainer>
      </form>
    </div>
  </div>
</template>

<style scoped>
.message-container {
    height: 80vh;
}
</style>
